import { Link } from "react-router-dom";
import { ReportCtas } from "@/components/access-report/ReportCtas";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import {
  formatPercentage,
  formatReportDate,
  useAccessReportIssues,
  type AccessReportIssueSummary,
} from "@/lib/access-report";
import { SEO, accessReportSeoMetadata } from "@/lib/seo";
import { buildAccessReportStructuredData } from "@/lib/structured-data";
import { FileSearch } from "lucide-react";
import { ListingSkeletons } from "@/components/ListingCard";
import { Button, EmptyState } from "@/components/system";

export default function AccessReportIndex() {
  const { city } = useCity();
  const issues = useAccessReportIssues(city.slug);
  const metadata = accessReportSeoMetadata();

  return (
    <PublicShell>
      {/* noindex,follow until Issue No.1 is published (SEO audit report 04). */}
      {issues.isSuccess ? (
        <SEO
          title={metadata.title}
          description={metadata.description}
          path={metadata.path}
          noindex={issues.data.issues.length === 0}
          follow
          jsonLd={buildAccessReportStructuredData()}
        />
      ) : null}
      <div className="ds-container">
        <header className="ds-page-head">
          <p className="ds-eyebrow ds-eyebrow--teal">Quarterly · citable · open data</p>
          <h1 className="ds-display-1">{city.name} Specialist Access Report</h1>
          <p className="ds-lead">
            PulseBoard's quarterly public dataset on specialist access in {city.name}: how findable specialists are,
            how many have confirmed live availability, and verified wait-time reports where the reporting threshold is met.
          </p>
        </header>

        <section className="ds-section ds-section--flush" aria-labelledby="access-issues-heading">
          <h2 id="access-issues-heading" className="ds-title">Published issues</h2>
          {issues.isLoading ? <ListingSkeletons count={2} /> : null}
          {issues.isError ? (
            <EmptyState
              tone="error"
              icon={<FileSearch />}
              title="The report archive didn't load"
              action={<Button variant="secondary" onClick={() => void issues.refetch()}>Try again</Button>}
            >
              Published reports stay frozen and citable. Check your connection and try again.
            </EmptyState>
          ) : null}
          {issues.data?.issues.length === 0 ? (
            <EmptyState icon={<FileSearch />} title={`The first ${city.name} baseline is on its way`}>
              Published issues appear here.
            </EmptyState>
          ) : null}
          {issues.data?.issues.length ? (
            <ol className="ds-stack" style={{ gap: 16, listStyle: "none", margin: 0, padding: 0 }}>
              {issues.data.issues.map((issue) => (
                <li key={issue.slug}>
                  <Link className="ds-card ds-tile" to={`/access-report/${issue.slug}`} aria-label={`Read ${issue.title}`}>
                    <p className="ds-eyebrow">Issue No.{issue.issueNo} · {formatPeriod(issue)}</p>
                    <h3 className="ds-title">{issue.title}</h3>
                    <p className="ds-small">{headlineSummary(issue)}</p>
                    <span className="ds-tile__go">Read report</span>
                  </Link>
                </li>
              ))}
            </ol>
          ) : null}
        </section>

        <section className="ds-section">
          <p className="ds-fine">
            Data © PulseBoard {city.name}. Published under{" "}
            <a className="ds-link" href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>.
          </p>
          <ReportCtas />
        </section>
      </div>
    </PublicShell>
  );
}

function formatPeriod(issue: AccessReportIssueSummary): string {
  return `${formatReportDate(issue.period.from, true)} – ${formatReportDate(issue.period.to, true)}`;
}

function headlineSummary(issue: AccessReportIssueSummary): string {
  const coverage = issue.headline.liveCoverage;
  return `${coverage.confirmed} of ${coverage.total} listings have confirmed live availability (${formatPercentage(coverage.pct)}); ${issue.headline.waitReports.received} wait report${issue.headline.waitReports.received === 1 ? "" : "s"} received.`;
}
